import { ReactElement, useEffect, useState } from 'react'
import ArticlesLayout from '../../components/layout/ArticlesLayout'
import { NextPageWithLayout } from '../_app'
import dayjs from 'dayjs'
import ReactMarkdown from 'react-markdown'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { Container } from '../../components/articles/Container'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import remarkBreaks from 'remark-breaks'
import Titles from '../../components/navigation/Titles'
import { BlogResponse } from '../../types/blog/blog-response'
import Image from 'next/image'
import SEO from '../../next-seo.config'
import { DefaultSeo } from 'next-seo'
import { createBucketClient } from '@cosmicjs/sdk'
import { useRouter } from 'next/router'

dayjs.extend(utc)
dayjs.extend(tz)
dayjs.extend(localizedFormat)

const Blog: NextPageWithLayout<{ posts: BlogResponse[] }> = ({ posts }) => {
  const [date, setDate] = useState('')
  useEffect(() => {
    setDate(dayjs.utc(posts[0].published_at).tz(dayjs.tz.guess()).format('ll'))
  }, [])

  const router = useRouter()

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <div></div>
  }

  return (
    <>
      <DefaultSeo
        {...SEO}
        title={posts[0].title}
        description={posts[0].metadata.excerpt}
      />

      {/*@ts-ignore*/}
      <Container className='mt-8'>
        <div className='xl:relative'>
          <div className={'mt-14'}>
            <Titles />
          </div>

          <div className='mx-auto mt-12'>
            <article>
              <header className='flex flex-col'>
                <time className='order-first flex items-center text-base text-zinc-400 dark:text-zinc-500'>
                  <span className='h-4 w-0.5 rounded-full bg-zinc-200 dark:bg-zinc-500' />
                  <span className='ml-3'>{date}</span>
                </time>
              </header>
            </article>
          </div>
        </div>

        <div className={'article mx-auto'}>
          <div className={'relative h-96 w-full shrink-0 rounded-full'}>
            <Image
              src={posts[0].metadata.cover_image.url}
              alt={'Cover image'}
              fill={true}
              className={'rounded-xl object-cover'}
            />
          </div>

          <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            className={'markdown text-lg-normal text-darul-0'}
            linkTarget={'_blank'}
          >
            {posts[0].metadata.content}
          </ReactMarkdown>
        </div>
      </Container>
    </>
  )
}

Blog.getLayout = function getLayout(page: ReactElement) {
  return <ArticlesLayout>{page}</ArticlesLayout>
}

export async function getStaticPaths() {
  try {
    const bucket = createBucketClient({
      bucketSlug: process.env.NEXT_PUBLIC_COSMIC_BUCKET_SLUG!,
      readKey: process.env.NEXT_PUBLIC_COSMIC_READ_KEY!,
    })
    const data = await bucket.objects
      .find({
        type: 'posts',
      })
      .props('slug,title,content,metadata') // Limit the API response data by props
    const posts: BlogResponse[] = await data.objects

    const paths = posts?.map((blog) => ({
      params: { slug: blog.slug },
    }))

    return {
      paths,
      fallback: true,
    }
  } catch (error) {
    console.error(error)
    return {
      paths: [],
      fallback: true,
    }
  }
}

type Props = {
  params: any
  locale: string
}

export async function getStaticProps({ params, locale }: Props) {
  try {
    const bucket = createBucketClient({
      bucketSlug: process.env.NEXT_PUBLIC_COSMIC_BUCKET_SLUG!,
      readKey: process.env.NEXT_PUBLIC_COSMIC_READ_KEY!,
    })
    const data = await bucket.objects
      .find({
        type: 'posts',
        slug: params.slug,
      })
      .props('slug,title,content,metadata') // Limit the API response data by props

    if (!data) {
      return {
        notFound: true,
      }
    }

    const posts: BlogResponse[] = await data.objects

    if (!posts || posts.length === 0) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common', 'booking'])),
        posts: posts,
      },
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}

export default Blog
